import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, CloudUpload, CheckCircle2, XCircle, Trash2, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import * as outbox from "@/offline/outbox";
import * as masters from "@/offline/masters";
import { drainQueue } from "@/offline/sync";
import { setSimulateOffline, getSimulateOffline } from "@/offline/apiClient";
import { useSyncStatus, statusLabel } from "@/offline/useSyncStatus";
import { RUNTIME_INFO } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function SyncPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role_code === "SUPER_ADMIN";   // technical details are for admins only
  const s = useSyncStatus();
  const label = statusLabel(s);
  const [rows, setRows] = useState([]);
  const [mastersInfo, setMastersInfo] = useState([]);
  const [busy, setBusy] = useState(false);
  const [refreshingMaster, setRefreshingMaster] = useState(null);
  const [simulated, setSimulated] = useState(getSimulateOffline());
  const [progress, setProgress] = useState(null);

  const reload = useCallback(async () => {
    setRows(await outbox.listAll());
    const infos = await Promise.all(
      masters.listStores().map(async (store) => ({
        store,
        meta: await masters.metaFor(store),
        stale: await masters.isStale(store),
      }))
    );
    setMastersInfo(infos);
  }, []);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const iv = setInterval(reload, 4000);
    return () => clearInterval(iv);
  }, [reload]);

  const doSyncNow = async () => {
    setBusy(true);
    try {
      const summary = await drainQueue({ manual: true });
      toast.success(`Drain done: ${summary.success} ok · ${summary.backoff} retry · ${summary.failed_final} failed`);
      reload();
    } catch (e) {
      toast.error(`Sync failed: ${e?.message || e}`);
    } finally { setBusy(false); }
  };

  // One simple action for everyone: upload anything waiting, then re-download the latest
  // data. Failures of single uploads are retried quietly in the background.
  const doRefreshData = async () => {
    setBusy(true);
    setProgress(null);
    try {
      try { await drainQueue({ manual: true }); } catch { /* retried automatically later */ }
      await masters.refreshAll({ force: true, onProgress: (p) => setProgress(p) });
      toast.success("Data refreshed");
      reload();
    } catch (e) {
      toast.error("Could not refresh the data. Check your internet connection and try again.");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const doRefreshMaster = async (store) => {
    setRefreshingMaster(store);
    setProgress(null);
    try {
      const r = await masters.refreshOne(store, {
        force: true,
        onProgress: (p) => setProgress(p),
      });
      toast.success(isAdmin ? `${store}: ${r.fetched ?? 0} items cached` : "Refreshed");
      reload();
    } catch (e) {
      toast.error(isAdmin ? `${store}: ${e?.message || e}` : "Could not refresh. Check your internet connection and try again.");
    } finally {
      setRefreshingMaster(null);
      setProgress(null);
    }
  };

  const doRefreshAll = async () => {
    setBusy(true);
    setProgress(null);
    try {
      await masters.refreshAll({ force: true, onProgress: (p) => setProgress(p) });
      toast.success(isAdmin ? "All masters refreshed" : "Data refreshed");
      reload();
    } catch (e) {
      toast.error(isAdmin ? `Refresh failed: ${e?.message || e}` : "Could not refresh the data. Check your internet connection and try again.");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const doRetry = async (row) => {
    await outbox.retryOne(row._id);
    reload();
    drainQueue().catch(() => {});
  };
  const doDiscard = async (row) => {
    await outbox.discard(row._id);
    reload();
  };
  const doForceReplay = async (row) => {
    // Reset a "success" item back to pending — this is the "Force replay" dev button
    // that lets us prove idempotency (server should return the SAME record).
    await outbox.retryOne(row._id);
    reload();
    drainQueue().catch(() => {});
  };

  return (
    <div className="space-y-5" data-testid="sync-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-neutral-500 hover:text-[#6D2C91] inline-flex items-center text-sm">
            <ChevronLeft className="h-4 w-4" /> Back
          </Link>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-neutral-400">Sync</div>
            <h1 className="text-2xl font-extrabold tracking-tight text-neutral-900">
              {isAdmin ? "Offline queue & masters" : "Refresh data"}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <div className="flex items-center gap-2 text-xs bg-white border border-neutral-200 rounded-lg px-3 py-2">
              <span className="text-neutral-500">Simulate offline</span>
              <Switch
                data-testid="sim-offline-switch"
                checked={simulated}
                onCheckedChange={(v) => { setSimulateOffline(v); setSimulated(v); }}
              />
            </div>
          )}
          <Button
            onClick={isAdmin ? doSyncNow : doRefreshData}
            disabled={busy}
            className="tulsi-gradient text-white font-semibold"
            data-testid="sync-now-button"
          >
            {busy || s.syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {isAdmin ? "Sync now" : "Refresh data"}
          </Button>
        </div>
      </div>

      {!isAdmin && s.counts.pending > 0 && (
        <p className="text-xs text-neutral-500">{s.counts.pending} item{s.counts.pending === 1 ? "" : "s"} waiting to upload — they go up automatically when your internet is good.</p>
      )}

      {isAdmin && <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-5 flex flex-wrap gap-6">
          <Stat label="Status" value={label.text} />
          <Stat label="Queued" value={s.counts.pending} testid="stat-queued" />
          <Stat label="In-flight" value={s.counts.in_flight} />
          <Stat label="Success" value={s.counts.success} testid="stat-success" />
          <Stat label="Failed" value={s.counts.failed_final} testid="stat-failed" />
          <Stat label="Last sync" value={s.lastSyncAt ? new Date(s.lastSyncAt).toLocaleString() : "—"} />
        </CardContent>
      </Card>}

      {isAdmin && <Card className="border-neutral-200 rounded-2xl" data-testid="diagnostics-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Diagnostics</CardTitle>
        </CardHeader>
        <CardContent className="p-5 pt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-xs">
          <DiagRow label="Frontend origin" value={RUNTIME_INFO.window_origin} testid="diag-origin" />
          <DiagRow label="Configured REACT_APP_BACKEND_URL" value={RUNTIME_INFO.configured_backend_url || "(unset)"} testid="diag-backend-url" />
          <DiagRow label="Effective API base" value={RUNTIME_INFO.effective_api_base} testid="diag-api-base" />
          <DiagRow
            label="Routing mode"
            value={RUNTIME_INFO.same_origin ? "same-origin (relative /api)" : "cross-origin (absolute)"}
            testid="diag-routing-mode"
          />
        </CardContent>
      </Card>}

      <Card className="border-neutral-200 rounded-2xl">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">{isAdmin ? "Masters" : "Your data"}</CardTitle>
          <Button size="sm" variant="outline" onClick={doRefreshAll} disabled={busy} data-testid="refresh-all-masters">
            <RefreshCw className={`h-3.5 w-3.5 mr-2 ${busy ? "animate-spin" : ""}`} /> Refresh all
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-neutral-100">
            {mastersInfo.map((m) => (
              <div key={m.store} className="flex items-center justify-between px-5 py-3" data-testid={`master-${m.store}`}>
                <div>
                  <div className="font-semibold text-neutral-800 capitalize">{m.store}</div>
                  <div className="text-[11px] text-neutral-500">
                    {m.meta.itemCount ?? 0} cached · {m.meta.lastRefreshedAt ? new Date(m.meta.lastRefreshedAt).toLocaleString() : "never"} {m.stale ? "· STALE" : ""}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => doRefreshMaster(m.store)}
                  disabled={refreshingMaster === m.store}
                  data-testid={`refresh-master-${m.store}`}
                >
                  {refreshingMaster === m.store ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                </Button>
              </div>
            ))}
          </div>
          {progress && (
            <div className="px-5 py-2 text-xs text-neutral-500 bg-neutral-50 border-t border-neutral-100">
              {progress.store}: {progress.fetched} / {progress.total || "?"} …
            </div>
          )}
        </CardContent>
      </Card>

      {isAdmin && <Card className="border-neutral-200 rounded-2xl">
        <CardHeader>
          <CardTitle className="text-base">Outbox ({rows.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {rows.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-neutral-500" data-testid="outbox-empty">
              Nothing queued. All caught up.
            </div>
          )}
          {rows.length > 0 && (
            <div className="divide-y divide-neutral-100">
              {rows.map((r) => (
                <OutboxRow key={r._id} row={r} onRetry={doRetry} onDiscard={doDiscard} onForceReplay={doForceReplay} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="text-2xl font-extrabold tracking-tight text-neutral-900" data-testid={testid}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function DiagRow({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1 border-b border-dashed border-neutral-100 last:border-0">
      <span className="text-neutral-500 shrink-0">{label}</span>
      <span
        data-testid={testid}
        className="font-mono text-[11px] text-neutral-800 truncate text-right"
        title={String(value)}
      >
        {value}
      </span>
    </div>
  );
}

function OutboxRow({ row, onRetry, onDiscard, onForceReplay }) {
  const isPending = row.status === "pending";
  const isFailed = row.status === "failed_final";
  const isSuccess = row.status === "success";
  return (
    <div className="px-5 py-3 flex items-start justify-between gap-3" data-testid={`outbox-row-${row._id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700">{row.method}</span>
          <span className="font-mono text-xs text-neutral-700 truncate">{row.path}</span>
          {isSuccess && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="h-3 w-3 mr-1" /> success</Badge>}
          {isPending && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">pending · #{row.attemptCount}</Badge>}
          {row.status === "in_flight" && <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100">in flight</Badge>}
          {isFailed && <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><XCircle className="h-3 w-3 mr-1" /> failed</Badge>}
        </div>
        <div className="text-[11px] text-neutral-500 mt-1">
          {row.entity_type || "—"} · {new Date(row.enqueuedAt).toLocaleString()} · key {row.idempotency_key?.slice(0, 8)}
        </div>
        {row.lastError && <div className="text-[11px] text-red-500 mt-1">Error: {row.lastError}</div>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {isFailed && (
          <Button size="sm" variant="outline" onClick={() => onRetry(row)} data-testid={`outbox-retry-${row._id}`}>Retry</Button>
        )}
        {isSuccess && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onForceReplay(row)}
            title="Force replay — proves idempotency"
            data-testid={`outbox-force-replay-${row._id}`}
          >
            Force replay
          </Button>
        )}
        {!isSuccess && (
          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => onDiscard(row)} data-testid={`outbox-discard-${row._id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
